import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiArrowRight } from "react-icons/pi";

export default function ClassicHero({ content, onContentChange, isSelected }: SectionContentProps) {
    const primaryColor = content.colorPalette?.primary || "#3B82F6";
    const secondaryColor = content.colorPalette?.secondary || "#4F46E5";
    const fontFamily = content.fontFamily || "'Inter', sans-serif";
    const padding = content.padding || "normal";

    // Background style
    const bgStyle = content.bgStyle || "gradient";
    const getBgStyle = () => {
        if (content.backgroundColor) return { backgroundColor: content.backgroundColor };
        if (bgStyle === "solid") return { backgroundColor: `${primaryColor}05` };
        if (bgStyle === "pattern") {
            return {
                backgroundColor: "#ffffff",
                backgroundImage: `radial-gradient(${primaryColor}30 1px, transparent 1px), radial-gradient(${secondaryColor}30 1px, transparent 1px)`,
                backgroundSize: "24px 24px",
                backgroundPosition: "0 0, 12px 12px",
            };
        }
        return {
            background: `linear-gradient(135deg, ${primaryColor}05 0%, #ffffff 100%)`,
        };
    };

    const getPaddingClass = () => {
        if (padding === "compact") return "py-16";
        if (padding === "spacious") return "py-32";
        return "py-24";
    };

    return (
        <div
            className={`relative overflow-hidden ${getPaddingClass()} ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ fontFamily }}
        >
            {/* Background Image Layer */}
            {/* Base Background Layer (Always visible, acts as fallback/filler) */}
            <div
                className="absolute inset-0 z-0"
                style={getBgStyle()}
            />

            {(content.imageUrl || content.image) && (
                <>
                    <div
                        className="absolute inset-0 z-0 mx-auto max-w-[1400px]"
                        style={{
                            backgroundImage: `url(${content.imageUrl || content.image})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat"
                        }}
                    />
                    {/* Stronger overlay for readability when image is present */}
                    <div className="absolute inset-0 z-0 bg-white/30 backdrop-blur-[2px]"
                        style={{
                            background: `linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(248,250,252,0.8) 100%)`
                        }}
                    />
                </>
            )}

            {/* Decorative Background Elements (visible with or without image, but subtle) */}
            <div
                className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none"
                style={{
                    backgroundImage: `radial-gradient(${primaryColor} 0.5px, transparent 0.5px)`,
                    backgroundSize: "16px 16px",
                }}
            />

            <div className="container relative z-10 mx-auto px-6 text-center max-w-4xl">
                <span className="inline-block py-1.5 px-4 rounded-full bg-white border border-gray-100 shadow-sm text-sm font-semibold mb-8 text-gray-600 animate-[fade-in_1s_ease-out]">
                    <EditableText
                        value={content.tag || "Introducing V2.0"}
                        onChange={(val) => onContentChange?.({ ...content, tag: val })}
                        as="span"
                    />
                </span>

                <h1 className="text-5xl md:text-7xl font-extrabold text-gray-900 leading-tight mb-8 tracking-tight">
                    <EditableText
                        value={content.title || "Classic Hero Title"}
                        onChange={(val) => onContentChange?.({ ...content, title: val })}
                        as="span"
                    />
                    <br />
                    <span className="text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}>
                        <EditableText
                            value={content.highlightText || "Highlight Text"}
                            onChange={(val) => onContentChange?.({ ...content, highlightText: val })}
                            as="span"
                            placeholder="Highlight"
                        />
                    </span>
                </h1>

                <p className="text-xl md:text-2xl text-gray-600 mb-10 leading-relaxed max-w-2xl mx-auto">
                    <EditableText
                        value={content.subtitle || "A classic centered hero section perfect for SaaS and marketing sites."}
                        onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
                        as="span"
                    />
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
                    <a
                        href={onContentChange ? undefined : (content.primaryCtaLink || "#")}
                        target={content.primaryCtaLink?.startsWith('http') && !onContentChange ? "_blank" : undefined}
                        rel={content.primaryCtaLink?.startsWith('http') && !onContentChange ? "noopener noreferrer" : undefined}
                        onClick={(e) => onContentChange && e.preventDefault()}
                        className="px-8 py-4 rounded-full font-bold text-white shadow-xl transition-all hover:shadow-2xl hover:-translate-y-1 flex items-center gap-2 cursor-pointer inline-flex"
                        style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                    >
                        <EditableText
                            value={content.primaryCta || "Get Started Free"}
                            onChange={(val) => onContentChange?.({ ...content, primaryCta: val })}
                            as="span"
                        />
                        <PiArrowRight className="w-4 h-4" />
                    </a>
                    <a
                        href={onContentChange ? undefined : (content.secondaryCtaLink || "#")}
                        target={content.secondaryCtaLink?.startsWith('http') && !onContentChange ? "_blank" : undefined}
                        rel={content.secondaryCtaLink?.startsWith('http') && !onContentChange ? "noopener noreferrer" : undefined}
                        onClick={(e) => onContentChange && e.preventDefault()}
                        className="px-8 py-4 rounded-full font-bold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all cursor-pointer inline-flex items-center justify-center"
                    >
                        <EditableText
                            value={content.secondaryCta || "View Pricing"}
                            onChange={(val) => onContentChange?.({ ...content, secondaryCta: val })}
                            as="span"
                        />
                    </a>
                </div>

                {(content.usersText !== "" || content.ratingText !== "") && (
                    <div className="flex items-center justify-center gap-4 text-sm text-gray-500 mt-4">
                        {content.usersText !== "" && (
                            <div className="flex items-center gap-2">
                                <div className="flex -space-x-2">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div
                                            key={i}
                                            className="w-8 h-8 rounded-full border-2 border-white"
                                            style={{
                                                background: `linear-gradient(135deg, ${primaryColor}${80 - i * 15}, ${secondaryColor}${80 - i * 15})`,
                                            }}
                                        />
                                    ))}
                                </div>
                                <span className="font-medium text-gray-600">
                                    <EditableText
                                        value={content.usersText ?? "2,500+ users"}
                                        onChange={(val) => onContentChange?.({ ...content, usersText: val })}
                                        as="span"
                                    />
                                </span>
                            </div>
                        )}
                        {(content.usersText !== "" && content.ratingText !== "") && (
                            <div className="w-px h-6 bg-gray-200" />
                        )}
                        {content.ratingText !== "" && (
                            <div className="flex items-center gap-1.5">
                                <svg
                                    className="w-4 h-4 text-yellow-400"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                <span className="font-medium text-gray-600">
                                    <EditableText
                                        value={content.ratingText ?? "4.9/5 rating"}
                                        onChange={(val) => onContentChange?.({ ...content, ratingText: val })}
                                        as="span"
                                    />
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
